/**
 * Feature "7 Drip": UTM / drip tracking.
 *
 * A pure, dependency-free module for capturing UTM campaign params on landing,
 * persisting them so they survive the sign-up auth round-trip (sign-up redirects
 * away to an auth provider and back, dropping the original query string), and
 * threading them onto outbound deep links.
 *
 * SSR / prerender-safe: this code runs during static prerender where `window`
 * and `localStorage` are undefined. Every storage access is guarded; nothing
 * here ever throws. Storage-dependent functions are no-ops / return `{}` when
 * storage is unavailable.
 */

export interface UtmParams {
  source?: string // utm_source
  medium?: string // utm_medium
  campaign?: string // utm_campaign
  content?: string // utm_content
  term?: string // utm_term
}

export const UTM_STORAGE_KEY = 'gw.utm'

/** Mapping from the canonical `utm_*` query key to our short field name. */
const UTM_FIELDS: ReadonlyArray<readonly [keyof UtmParams, string]> = [
  ['source', 'utm_source'],
  ['medium', 'utm_medium'],
  ['campaign', 'utm_campaign'],
  ['content', 'utm_content'],
  ['term', 'utm_term'],
]

/**
 * Safe accessor for a Storage. Returns the explicitly-passed storage, otherwise
 * `globalThis.localStorage`, otherwise `undefined` if access throws (e.g.
 * sandboxed iframe, disabled cookies) or is absent (prerender).
 */
function getStorage(storage?: Storage): Storage | undefined {
  if (storage) return storage
  try {
    const ls = (globalThis as { localStorage?: Storage }).localStorage
    return ls ?? undefined
  } catch {
    return undefined
  }
}

/** Parse `"?utm_source=x&utm_content=y"` into a `UtmParams`. Ignores empties. */
export function parseUtm(search: string): UtmParams {
  const out: UtmParams = {}
  if (!search) return out
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    return out
  }
  for (const [field, key] of UTM_FIELDS) {
    const value = params.get(key)
    if (value !== null && value !== '') {
      out[field] = value
    }
  }
  return out
}

/** True if at least one UTM field is present (non-empty). */
export function hasAnyUtm(p: UtmParams): boolean {
  for (const [field] of UTM_FIELDS) {
    const value = p[field]
    if (value !== undefined && value !== '') return true
  }
  return false
}

/** Read the persisted UTM set. Returns `{}` if none / storage unavailable. */
export function loadUtm(storage?: Storage): UtmParams {
  const store = getStorage(storage)
  if (!store) return {}
  let raw: string | null
  try {
    raw = store.getItem(UTM_STORAGE_KEY)
  } catch {
    return {}
  }
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return {}
    const record = parsed as Record<string, unknown>
    const out: UtmParams = {}
    for (const [field] of UTM_FIELDS) {
      const value = record[field]
      if (typeof value === 'string' && value !== '') out[field] = value
    }
    return out
  } catch {
    return {}
  }
}

/**
 * Merge-persist `p` into storage, but ONLY when it carries at least one UTM
 * field. Existing persisted fields are never clobbered by empty/absent new
 * fields — present fields overlay, others are preserved.
 */
export function persistUtm(p: UtmParams, storage?: Storage): void {
  const store = getStorage(storage)
  if (!store) return
  if (!hasAnyUtm(p)) return
  const merged: UtmParams = { ...loadUtm(store) }
  for (const [field] of UTM_FIELDS) {
    const value = p[field]
    if (value !== undefined && value !== '') merged[field] = value
  }
  try {
    store.setItem(UTM_STORAGE_KEY, JSON.stringify(merged))
  } catch {
    // ignore quota / serialization failures
  }
}

/**
 * Parse the current URL's search string, persist any UTM params it carries,
 * then return the effective persisted set (so callers always get the full,
 * round-trip-surviving picture even when the current URL has no UTM params).
 */
export function captureFromSearch(search: string, storage?: Storage): UtmParams {
  const parsed = parseUtm(search)
  persistUtm(parsed, storage)
  return loadUtm(storage)
}

/**
 * Append any present `utm_*` params from `p` onto `url`. Preserves an existing
 * `#hash` (params go before it), handles an existing `?query` (appends with
 * `&`), and skips any utm key already present in the url so nothing duplicates.
 */
export function withUtm(url: string, p: UtmParams): string {
  if (!hasAnyUtm(p)) return url

  const hashIndex = url.indexOf('#')
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : ''
  const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url

  const queryIndex = base.indexOf('?')
  const existingQuery = queryIndex >= 0 ? base.slice(queryIndex + 1) : ''
  const path = queryIndex >= 0 ? base.slice(0, queryIndex) : base

  const existing = new URLSearchParams(existingQuery)
  const additions: string[] = []
  for (const [field, key] of UTM_FIELDS) {
    const value = p[field]
    if (value === undefined || value === '') continue
    if (existing.has(key)) continue
    additions.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
  }

  if (additions.length === 0) return url

  const query = existingQuery
    ? `${existingQuery}&${additions.join('&')}`
    : additions.join('&')
  return `${path}?${query}${hash}`
}
